import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAgentPermissions, getAgentPermissions } from "../../redux/agents/agentsSlice";
import { setGoingToClose, setHasToRefresh } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";
import Loader from "../Loader";
import DynamicIcon from "../DynamicIcon";

export default function AddEditAgent({ agentId }) {
    const dispatch = useDispatch();
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    const allPermissions = useSelector(selectAgentPermissions);

    const [agentData, setAgentData] = useState({
        agentName: '',
        agentType: '',
        description: '',
        permissions: [],
        active: true
    });
    const [isLoadingAgent, setIsLoadingAgent] = useState(false);
    const [sendFetchStatus, setSendFetchStatus] = useState('i'); // i=initial, l=loading, s=success, f=failure
    const [sendError, setSendError] = useState('');

    // AccountId per le credenziali agente
    const storedUser = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
    const accountId = storedUser.accountId || '';

    // Credenziali mostrate dopo creazione o rigenerazione secret
    const [credentials, setCredentials] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    // Stato per rigenerazione secret
    const [regenStatus, setRegenStatus] = useState('i');
    const [regenError, setRegenError] = useState('');

    // Carica permessi disponibili
    useEffect(() => {
        if (allPermissions.length === 0) {
            dispatch(getAgentPermissions({ token }));
        }
    }, [dispatch, token, allPermissions.length]);

    // Carica dettagli agente in modifica
    useEffect(() => {
        if (agentId) {
            const fetchAgent = async () => {
                setIsLoadingAgent(true);
                try {
                    const url = `${import.meta.env.VITE_API_BASE_URL}/agents/${agentId}`;
                    const response = await fetch(url, {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json', 'auth-token': token }
                    });
                    if (!response.ok) throw new Error("Errore nel caricamento dell'agente");
                    const data = await response.json();
                    setAgentData({
                        agentName: data.agentName || '',
                        agentType: data.agentType || '',
                        description: data.description || '',
                        permissions: data.permissions || [],
                        active: data.active !== false
                    });
                } catch (err) {
                    setSendError(err.message);
                } finally {
                    setIsLoadingAgent(false);
                }
            };
            fetchAgent();
        }
    }, [agentId, token]);

    // Raggruppa permessi per risorsa (escludo il wildcard *)
    const permissionsByResource = useMemo(() => {
        const groups = {};
        (allPermissions || []).forEach(perm => {
            if (perm === '*') return;
            const [action, resource] = perm.split(':');
            if (!groups[resource]) groups[resource] = [];
            groups[resource].push({ permission: perm, action });
        });
        return groups;
    }, [allPermissions]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setAgentData(prev => ({ ...prev, [id]: value }));
    };

    const handlePermissionToggle = (perm) => {
        setAgentData(prev => {
            const perms = prev.permissions.includes(perm)
                ? prev.permissions.filter(p => p !== perm)
                : [...prev.permissions, perm];
            return { ...prev, permissions: perms };
        });
    };

    const handleWildcardToggle = () => {
        setAgentData(prev => {
            if (prev.permissions.includes('*')) {
                return { ...prev, permissions: [] };
            }
            return { ...prev, permissions: ['*'] };
        });
    };

    const handleActiveToggle = () => {
        setAgentData(prev => ({ ...prev, active: !prev.active }));
    };

    const handleSelectAllResource = (resource) => {
        const resourcePerms = permissionsByResource[resource].map(p => p.permission);
        setAgentData(prev => {
            const allSelected = resourcePerms.every(p => prev.permissions.includes(p));
            let newPerms;
            if (allSelected) {
                newPerms = prev.permissions.filter(p => !resourcePerms.includes(p));
            } else {
                newPerms = [...new Set([...prev.permissions, ...resourcePerms])];
            }
            return { ...prev, permissions: newPerms };
        });
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const handleSubmit = async () => {
        if (!agentData.agentName.trim()) {
            setSendError("Il nome dell'agente è obbligatorio");
            setSendFetchStatus('f');
            return;
        }

        setSendFetchStatus('l');
        setSendError('');

        try {
            const isEdit = !!agentId;
            const url = isEdit
                ? `${import.meta.env.VITE_API_BASE_URL}/agents/${agentId}`
                : `${import.meta.env.VITE_API_BASE_URL}/agents`;

            const body = {
                agentName: agentData.agentName,
                agentType: agentData.agentType || 'generic',
                description: agentData.description,
                permissions: agentData.permissions
            };
            if (isEdit) body.active = agentData.active;

            const response = await fetch(url, {
                method: isEdit ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json', 'auth-token': token },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Errore da parte del server.");
            }

            const result = await response.json();

            // In creazione mostra credenziali
            if (!isEdit && result.agentKey && result.agentSecret) {
                setCredentials({
                    agentKey: result.agentKey,
                    agentSecret: result.agentSecret
                });
            }

            setSendFetchStatus('s');
            dispatch(setHasToRefresh());

            // Se è modifica chiudi dopo 1.5s, se è creazione resta aperto per mostrare credenziali
            if (isEdit) {
                setTimeout(() => {
                    dispatch(setGoingToClose());
                }, 1500);
            }
        } catch (error) {
            setSendFetchStatus('f');
            setSendError(error.message || "Errore da parte del server.");
        }
    };

    const handleRegenerateSecret = async () => {
        setRegenStatus('l');
        setRegenError('');

        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/agents/${agentId}/regenerate-secret`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'auth-token': token }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Errore nella rigenerazione del secret");
            }

            const result = await response.json();
            setCredentials({
                agentKey: result.agentKey,
                agentSecret: result.agentSecret
            });
            setRegenStatus('s');
        } catch (error) {
            setRegenError(error.message);
            setRegenStatus('f');
        }
    };

    const isWildcard = agentData.permissions.includes('*');

    return (
        <div className="w-full p-8">
            {isLoadingAgent && <LoadingSection isFixed={false} />}

            {/* Box credenziali (dopo creazione o rigenerazione) */}
            {credentials && (
                <div className="flex flex-col gap-4 mt-8 border-2 rounded-lg p-4 border-green-300 bg-green-50 relative">
                    <h2 className="text-lg font-bold flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-key" /> Credenziali Agente
                    </h2>
                    <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded text-sm">
                        <DynamicIcon iconClass="fa-solid fa-triangle-exclamation" /> Copia e conserva il secret adesso. Non sarà più visibile dopo la chiusura di questa finestra.
                    </div>

                    <div className="input-box">
                        <label>Agent Key</label>
                        <div className="flex gap-2 items-center">
                            <input type="text" value={credentials.agentKey} readOnly className="flex-1 font-mono text-sm" />
                            <button
                                className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 whitespace-nowrap"
                                onClick={() => copyToClipboard(credentials.agentKey, 'key')}
                            >
                                {copiedField === 'key' ? 'Copiato!' : 'Copia'}
                            </button>
                        </div>
                    </div>

                    <div className="input-box">
                        <label>Agent Secret</label>
                        <div className="flex gap-2 items-center">
                            <input type="text" value={credentials.agentSecret} readOnly className="flex-1 font-mono text-sm" />
                            <button
                                className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 whitespace-nowrap"
                                onClick={() => copyToClipboard(credentials.agentSecret, 'secret')}
                            >
                                {copiedField === 'secret' ? 'Copiato!' : 'Copia'}
                            </button>
                        </div>
                    </div>

                    <div className="input-box">
                        <label>Account ID</label>
                        <div className="flex gap-2 items-center">
                            <input type="text" value={accountId} readOnly className="flex-1 font-mono text-sm" />
                            <button
                                className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 whitespace-nowrap"
                                onClick={() => copyToClipboard(accountId, 'accountId')}
                            >
                                {copiedField === 'accountId' ? 'Copiato!' : 'Copia'}
                            </button>
                        </div>
                    </div>

                    {/* Esempio d'uso */}
                    <div className="mt-2">
                        <label className="font-medium text-sm">Esempio d'uso (curl)</label>
                        <div className="relative">
                            <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto whitespace-pre">{`curl -H "x-agent-key: ${credentials.agentKey}" \\
     -H "x-agent-secret: ${credentials.agentSecret}" \\
     -H "x-account-id: ${accountId}" \\
     ${import.meta.env.VITE_API_BASE_URL}/customers/list`}</pre>
                            <button
                                className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                onClick={() => copyToClipboard(
                                    `curl -H "x-agent-key: ${credentials.agentKey}" -H "x-agent-secret: ${credentials.agentSecret}" -H "x-account-id: ${accountId}" ${import.meta.env.VITE_API_BASE_URL}/customers/list`,
                                    'curl'
                                )}
                            >
                                {copiedField === 'curl' ? 'Copiato!' : 'Copia'}
                            </button>
                        </div>
                    </div>

                    {/* Se siamo in creazione e le credenziali sono visibili, mostra pulsante per chiudere */}
                    {!agentId && sendFetchStatus === 's' && (
                        <button
                            className="primary mt-2"
                            onClick={() => dispatch(setGoingToClose())}
                        >
                            Ho copiato le credenziali, chiudi
                        </button>
                    )}
                </div>
            )}

            {/* Form */}
            {(!credentials || agentId) && (
                <div className="flex flex-col gap-4 mt-8 border-2 rounded-lg p-4 border-blue-200 relative">
                    <h1>{agentId ? 'Modifica Agente' : 'Nuovo Agente'}</h1>

                    {/* Nome e Tipo */}
                    <div className="flex gap-2">
                        <div className="input-box flex-1">
                            <label htmlFor="agentName">Nome Agente <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="agentName"
                                value={agentData.agentName}
                                onChange={handleInputChange}
                                placeholder="Es. Agente Manutenzioni"
                            />
                        </div>
                        <div className="input-box flex-1">
                            <label htmlFor="agentType">Tipo</label>
                            <input
                                type="text"
                                id="agentType"
                                value={agentData.agentType}
                                onChange={handleInputChange}
                                placeholder="Es. maintenance, generic"
                            />
                        </div>
                    </div>

                    {/* Descrizione */}
                    <div className="input-box">
                        <label htmlFor="description">Descrizione</label>
                        <textarea
                            id="description"
                            value={agentData.description}
                            onChange={handleInputChange}
                            rows={3}
                            placeholder="Descrizione delle funzionalità dell'agente..."
                            className="w-full p-2 border rounded"
                        />
                    </div>

                    {/* Toggle Attivo (solo modifica) */}
                    {agentId && (
                        <div className="flex items-center gap-3">
                            <label className="font-medium">Stato:</label>
                            <button
                                type="button"
                                className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
                                    agentData.active
                                        ? 'bg-green-500 text-white'
                                        : 'bg-red-500 text-white'
                                }`}
                                onClick={handleActiveToggle}
                            >
                                {agentData.active ? 'Attivo' : 'Disattivato'}
                            </button>
                        </div>
                    )}

                    {/* Permessi */}
                    <div>
                        <h2 className="mb-2">Permessi</h2>

                        {/* Wildcard */}
                        <div className="mb-3 p-3 border rounded bg-gray-50">
                            <label className="flex items-center gap-2 cursor-pointer font-medium">
                                <input
                                    type="checkbox"
                                    checked={isWildcard}
                                    onChange={handleWildcardToggle}
                                    className="w-4 h-4"
                                />
                                <DynamicIcon iconClass="fa-solid fa-star" />
                                Accesso completo (*)
                            </label>
                            <p className="text-sm text-gray-500 ml-6">Permette l'accesso a tutte le risorse</p>
                        </div>

                        {/* Permessi per risorsa */}
                        {!isWildcard && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(permissionsByResource).map(([resource, perms]) => {
                                    const allSelected = perms.every(p => agentData.permissions.includes(p.permission));
                                    return (
                                        <div key={resource} className="border rounded p-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium capitalize">{resource}</span>
                                                <button
                                                    type="button"
                                                    className="text-xs text-blue-500 hover:underline"
                                                    onClick={() => handleSelectAllResource(resource)}
                                                >
                                                    {allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
                                                </button>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {perms.map(({ permission, action }) => (
                                                    <label key={permission} className="flex items-center gap-1 cursor-pointer text-sm">
                                                        <input
                                                            type="checkbox"
                                                            checked={agentData.permissions.includes(permission)}
                                                            onChange={() => handlePermissionToggle(permission)}
                                                            className="w-3.5 h-3.5"
                                                        />
                                                        {action}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Rigenera Secret (solo modifica) */}
                    {agentId && (
                        <div className="border-t pt-4 mt-2">
                            <h2 className="mb-2">Sicurezza</h2>
                            <div className="flex items-center gap-3">
                                <button
                                    className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors flex items-center gap-2 text-sm"
                                    onClick={handleRegenerateSecret}
                                    disabled={regenStatus === 'l'}
                                >
                                    <DynamicIcon iconClass="fa-solid fa-rotate" />
                                    Rigenera Secret
                                </button>
                                {regenStatus === 'l' && <Loader />}
                                {regenStatus === 's' && <span className="text-green-600 text-sm">Secret rigenerato! Vedi sopra.</span>}
                                {regenStatus === 'f' && <span className="text-red-600 text-sm">{regenError}</span>}
                            </div>
                        </div>
                    )}

                    {/* Errore */}
                    {sendError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            {sendError}
                        </div>
                    )}

                    {/* Azioni */}
                    <div className="flex gap-2 h-[40px]">
                        {(sendFetchStatus !== 'l' && sendFetchStatus !== 's') || sendError ? (
                            <div className="flex gap-2">
                                <button className="primary" onClick={handleSubmit}>
                                    {agentId ? 'Salva Agente' : 'Crea Agente'}
                                </button>
                                <button className="secondary" onClick={() => dispatch(setGoingToClose())}>
                                    Annulla
                                </button>
                            </div>
                        ) : null}
                        {sendFetchStatus === 'l' && !sendError && <Loader />}
                        {sendFetchStatus === 's' && !sendError && !credentials && (
                            <h3>Agente {agentId ? 'aggiornato' : 'creato'} con successo.</h3>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
